import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import MapView, { Marker, Region } from 'react-native-maps';
import { MAP_PROVIDER } from '../../config/maps';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import { watchRide, type RideTracking } from '../../services/rideTracking';
import { colors, hairline, radii, spacing } from '../../theme';

/**
 * Where the car is, right now.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS DOES NOT SHOW ON AN ORDINARY TRIP
 * ---------------------------------------------------------------------------
 * `BookingDetailScreen` carries a long note explaining why it draws no map: a
 * route nobody is feeding is an invented route, and an invented route is worse
 * than none because it looks like tracking and therefore gets believed.
 *
 * This one is fed. Every pin on it is a position the driver's own handset
 * reported, and when that stops arriving the map says so rather than leaving
 * the marker where it was - which is the same failure in a different costume.
 *
 * It is mounted by `SosScreen` once an alarm is live, and by nothing else. On
 * an ordinary trip the driver card's text is the honest surface: somebody
 * watching a car approach does not need a map, and a customer who has pressed
 * a panic button does.
 *
 * ---------------------------------------------------------------------------
 * THE MAP FOLLOWS, IT DOES NOT FIGHT
 * ---------------------------------------------------------------------------
 * Uncontrolled `initialRegion` plus `animateToRegion` through the ref, which
 * is `LocationScreen`'s arrangement and for the reason set out there: a
 * controlled `region` re-centres on every render and takes the map away from a
 * finger that is dragging it. Somebody trying to see where a car is going
 * should be able to pan.
 */

/** Enough context around the pin to see which way the car is pointed. */
const SPAN = { latitudeDelta: 0.012, longitudeDelta: 0.012 };

const regionFor = (position: { lat: number; lng: number }): Region => ({
  latitude: position.lat,
  longitude: position.lng,
  ...SPAN,
});

function RideTrackingMap({
  rideId,
  /** Told every reading, so the parent can render the caveats beside the map. */
  onView,
}: {
  rideId: string;
  onView?: (view: RideTracking) => void;
}) {
  const [view, setView] = useState<RideTracking | null>(null);
  const [gone, setGone] = useState(false);
  const mapRef = useRef<MapView>(null);

  /*
   * Through a ref so the watcher is started once per ride rather than torn
   * down and rebuilt whenever the parent re-renders with a fresh callback -
   * a long poll restarted on every draw never completes a hold.
   */
  const notify = useRef(onView);
  notify.current = onView;

  useEffect(() => {
    setView(null);
    setGone(false);

    const stop = watchRide(rideId, {
      onView: next => {
        setView(next);
        notify.current?.(next);
        if (next.position) {
          // Animated rather than snapped: a jump reads as a glitch, and a
          // slide reads as movement, which is what it is.
          mapRef.current?.animateToRegion(regionFor(next.position), 500);
        }
      },
      onGone: () => setGone(true),
    });

    return stop;
  }, [rideId]);

  if (gone) {
    return (
      <View style={styles.placeholder}>
        <Icon name="pin" size={20} color={colors.inkMuted} />
        <Text variant="caption" align="center" color={colors.inkMuted}>
          This trip has ended, so there is nothing left to follow. Your alert is
          still open with our safety team.
        </Text>
      </View>
    );
  }

  if (!view) {
    return (
      <View style={styles.placeholder}>
        <ActivityIndicator color={colors.primary} />
        <Text variant="caption" color={colors.inkMuted}>
          Finding your driver…
        </Text>
      </View>
    );
  }

  /*
   * No position at all - the driver's phone has never reported, or there is no
   * driver yet. Said in words rather than drawn as an empty map centred on the
   * ocean, which is what a map with nothing to plot actually looks like.
   */
  if (!view.position) {
    return (
      <View style={styles.placeholder}>
        <Icon name="pin" size={20} color={colors.inkMuted} />
        <Text variant="caption" align="center" color={colors.inkMuted}>
          We cannot see your driver's location right now. Our safety team has
          your own location and the trip details.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      <MapView
        ref={mapRef}
        provider={MAP_PROVIDER}
        style={StyleSheet.absoluteFill}
        initialRegion={regionFor(view.position)}
        showsMyLocationButton={false}
        toolbarEnabled={false}
        pointerEvents="auto"
      >
        <Marker
          coordinate={{
            latitude: view.position.lat,
            longitude: view.position.lng,
          }}
          title="Your driver"
          // Greyed while the fix is stale, so the marker itself carries the
          // caveat rather than relying on somebody reading the strip below.
          pinColor={view.isStale ? colors.inkMuted : colors.danger}
        />
        <Marker
          coordinate={{
            latitude: view.drop.lat,
            longitude: view.drop.lng,
          }}
          title="Where you are going"
          description={view.drop.addressText ?? undefined}
          pinColor={colors.primary}
        />
      </MapView>

      {/*
        A stale pin reads as "they've parked", not "we lost them". On a moving
        vehicle during an emergency that is the misreading that matters, so it
        is stated over the map rather than left to the marker's colour.
      */}
      {view.isStale ? (
        <View style={styles.staleStrip}>
          <Icon name="clock" size={13} color={colors.onDanger} />
          <Text variant="caption" color={colors.onDanger}>
            Your driver's phone has stopped reporting. This is their last known
            position.
          </Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    height: 260,
    borderRadius: radii.lg,
    overflow: 'hidden',
    backgroundColor: colors.surfaceMuted,
    ...hairline,
    borderColor: colors.border,
  },
  placeholder: {
    height: 140,
    borderRadius: radii.lg,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.lg,
    backgroundColor: colors.surfaceMuted,
    ...hairline,
    borderColor: colors.border,
  },
  staleStrip: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.danger,
  },
});

export default RideTrackingMap;
